import React, { useMemo } from "react";
import { cn, formatAddress, formatDateWithOrdinal } from "@/lib/utils";
import { useGetAgreementQuery } from "@/store/agreements/apis";
import { useParams } from "react-router-dom";
import { MOVE_KINDS_SHORT, SERVICE_TYPES } from "@/constants";
import RateCharges from "./RateCharges";
import { format, parseISO } from "date-fns";
import { Agreement, Stop } from "@/types";
import { LoadingText } from "@/components/LoadingText";
import Error from "@/components/Error";

const BOLCell = ({
  label,
  value,
  borderRight,
}: {
  label: string;
  value: string | string[];
  borderRight?: boolean;
}) => {
  return (
    <div
      className={cn(
        "flex flex-col py-1.5 gap-1 min-h-[50px]",
        borderRight && "border-r border-gray-900"
      )}
    >
      <div className="text-xs font-mono uppercase font-semibold">{label}</div>
      <div className="text-base font-medium font-typewriter whitespace-pre-wrap">
        {Array.isArray(value) ? value.join("\r\n") : value}
      </div>
    </div>
  );
};

const SignatureArea = ({
  agreement,
  label,
  position,
  signed_by,
}: {
  agreement: Agreement;
  label: string;
  position: string;
  signed_by: "customer" | "mover";
}) => {
  const handleSignatureClick = () => {
    // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
    if (window["ReactNativeWebView"]) {
      // @ts-expect-error - window["ReactNativeWebView"] is not defined in the browser
      window["ReactNativeWebView"].postMessage(
        JSON.stringify({
          type: "signature_requested",
          position: position,
          label: label,
          signed_by,
        })
      );
    }
  };

  const signature = useMemo(() => {
    return agreement.signatures.find(
      (signature) =>
        signature.position === position && signature.signed_by === signed_by
    );
  }, [agreement.signatures, position, signed_by]);

  return (
    <div
      className={cn(
        "flex flex-col justify-between border border-gray-900 px-2 py-2 bg-amber-50",
        signature && "border-none bg-white",
        !signature && "cursor-pointer"
      )}
      onClick={() => (signature ? void 0 : handleSignatureClick())}
    >
      <div className="flex items-center gap-2">
        {signature ? (
          <div className="flex items-center gap-2">
            <img
              src={
                signature.blob.startsWith("data:")
                  ? signature.blob
                  : `data:image/png;base64,${signature.blob}`
              }
              alt="Signature"
              className="h-16 rounded-full"
            />
          </div>
        ) : (
          <div className="flex items-center gap-2">Tap and sign</div>
        )}
      </div>
      {signature ? (
        <p className="text-sm">
          {signature.name} signed on{" "}
          {format(parseISO(signature.created_at), "MM/dd/yyyy hh:mm a")}
        </p>
      ) : (
        <p className="text-sm text-gray-500">{label}</p>
      )}
    </div>
  );
};

const BillOfLading: React.FC = () => {
  const { ref } = useParams();
  const { data, isLoading } = useGetAgreementQuery(ref || "", {
    skip: !ref,
  });

  const agreement = useMemo(() => {
    return data?.agreement;
  }, [data]);

  const move = useMemo(() => {
    return data?.move;
  }, [data]);

  const mover = useMemo(() => {
    return data?.mover;
  }, [data]);

  if (isLoading || !agreement)
    return <LoadingText text="Loading agreement details" />;

  if (!mover)
    return (
      <Error error="There was a problem loading this agreement. Please call us and we'll get this sorted out for you." />
    );

  if (!move)
    return (
      <Error error="There was a problem loading this agreement. Please call us and we'll get this sorted out for you." />
    );

  return (
    <div className="py-4 px-6 lg:px-4">
      <div className="p-0 lg:p-4 font-paper max-w-[11in] mx-auto lg:border lg:border-gray-900">
        <div className="flex flex-col lg:flex-row lg:justify-between gap-4 lg:gap-0 items-center lg:items-start pb-4 border-b border-gray-900">
          <div>
            <img
              src={`${mover.logo_url}`}
              alt="Logo"
              className="w-24 object-contain"
            />
          </div>
          <div className="text-center lg:text-right">
            <div className="text-base font-semibold mb-1">{mover.name}</div>
            <div className="text-sm leading-snug font-mono">
              <div>
                {mover.street}, {mover.city} {mover.state} {mover.zip}
              </div>
              <div>{mover.email}</div>
              <div>{mover.business_phone}</div>
            </div>
          </div>
        </div>

        <div className="py-4 text-center border-b border-gray-900 space-y-2">
          <h1 className="font-paper text-2xl font-semibold text-sky-800">
            Household Goods Bill of Lading / Contract for Moving Services
          </h1>
          <p className="text-sm font-mono">
            Generated on {format(parseISO(agreement?.created_at), "MM/dd/yyyy")}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6 text-sm leading-relaxed border-b border-gray-900">
          <BOLCell label="Shipper" value={move.customer_name} borderRight />
          <BOLCell label="Job Number" value={move.number} borderRight />
          <BOLCell
            label="Estimate"
            value={
              move.billing_type === "hourly"
                ? "Hourly (Non Binding)"
                : "Flat Rate (Binding)"
            }
          />
        </div>

        <div className="grid grid-cols-3 gap-6 text-sm leading-relaxed border-b border-gray-900">
          <BOLCell
            label="Service"
            value={
              SERVICE_TYPES[move.service as keyof typeof SERVICE_TYPES] ||
              move.service ||
              "N/A"
            }
            borderRight
          />
          <BOLCell
            label="Service Date(s)"
            value={formatDateWithOrdinal(new Date(move.service_date))}
            borderRight
          />
          <BOLCell
            label="Details"
            value={MOVE_KINDS_SHORT[move.kind as keyof typeof MOVE_KINDS_SHORT]}
          />
        </div>
        <div className="grid grid-cols-3 gap-6 text-sm leading-relaxed border-b border-gray-900">
          <BOLCell
            label="Origin"
            value={formatAddress(move.origin)}
            borderRight
          />
          <BOLCell
            label="Intermediate Stops"
            value={
              move.stops?.length
                ? move.stops?.map((stop: Stop) => formatAddress(stop))
                : "N/A"
            }
            borderRight
          />
          <BOLCell
            label="Destination"
            value={formatAddress(move.destination)}
          />
        </div>

        <div className="grid grid-cols-2 gap-6 text-sm leading-relaxed border-b border-gray-900">
          <BOLCell label="Notes" value={move.notes_customer} />
        </div>

        <div className="py-4">
          <p className="font-paper">
            This bill of lading establishes a contract between you (
            {move.customer_name}) and the household goods carrier, {mover.name}{" "}
            (mover). It authorizes the mover to perform the services listed in
            this agreement. Please make sure you read and understand the terms
            and conditions included. This contract only covers the listed items
            and services. Additional items and/or services may result in
            additional cost.
          </p>
        </div>

        <div className="py-4 border-t border-gray-900">
          <h3 className="font-semibold text-base">
            Shipper's Authorization (prior to loading)
          </h3>
          <p className="font-paper">
            Shipper authorizes the mover to perform the moving services as
            listed here and agrees to the terms and conditions of this contract.
          </p>
          <div className="mt-2">
            <div className="flex items-center">
              <SignatureArea
                agreement={agreement}
                label="Shipper's Signature (pre move)"
                position="authorization"
                signed_by="customer"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          <div className="space-y-4 w-full lg:w-1/2">
            <div>
              <h4 className="font-paper text-base font-medium">
                Carrier's Liability
              </h4>
              <p className="font-paper text-sm">
                By default, Basic Value Protection is included in your move, and
                covers your items at $0.60 per pound per article, at no cost to
                you. This means that you'll be paid 60 cents per pound for the
                estimated weight of a lost or damaged item, regardless of the
                actual value of the item.
                <br />
                Note: Valuation is not insurance coverage. We suggest you
                purchase third-party insurance to protect your property.
              </p>
            </div>
            <div>
              <h4 className="font-paper text-base font-medium">
                Payment Terms
              </h4>
              <p className="font-paper text-sm">
                Payment and any balances for the move are due in full upon
                completion of the job while crew is on site.
              </p>
            </div>
            <div>
              {mover?.state === "CA" && (
                <>
                  <h4 className="font-paper text-base font-medium">
                    Double Drive Time (California Specific)
                  </h4>
                  <p className="font-paper text-sm">
                    As per CA state regulations, carriers are required to charge
                    Double Drive Time for hourly moves. This is calculated as
                    twice the drive time it takes to travel from the point of
                    origin (pickup) of the move to the final destination charged
                    at the hourly labor rate of the move.
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="w-full lg:w-1/2">
            <div>
              <h4 className="font-paper text-lg font-medium">
                Rate and Charges
              </h4>
              <RateCharges move={move} />
            </div>
          </div>
        </div>

        <div
          className={cn(
            "mt-4 py-4 border-t border-gray-900 hidden",
            move.status === "completed" && "block"
          )}
        >
          <p className="font-paper">
            I acknowledge that I have received the services as described in this
            agreement and accept delivery of my goods and/or services.
          </p>
          <p className="mt-2 font-paper text-sm">
            NOTICE: Please inspect your goods promptly. Claims for lost or
            damaged items must be filed in writing and within 90 days of
            delivery.
          </p>
          <div className="mt-2">
            <div className="flex items-center gap-8">
              <SignatureArea
                agreement={agreement}
                label="Customer Signature (post move)"
                position="completion"
                signed_by="customer"
              />
              <SignatureArea
                agreement={agreement}
                label="Mover Signature (post move)"
                position="completion"
                signed_by="mover"
              />
            </div>
          </div>
        </div>

        {/* Update Terms and Conditions section */}
        <section
          className="mt-24 border-t border-gray-900 pt-4 space-y-4"
          style={{ pageBreakBefore: "always" }}
        >
          <h2 className="text-lg text-center font-paper font-bold mb-3">
            TERMS AND CONDITIONS
          </h2>

          <p className="font-mono">
            <span className="font-bold">
              (1) Section 1 - General Provisions.
            </span>
            <br />
            (A) For the purposes of this subsection, the following terms will
            mean: (i) Household goods carrier--The motor carrier/mover
            contracted to transport a shipment of household goods. (ii)
            Shipper--The owner of the household goods shipment or his
            representative.
            <br />
            (B) Changes to the moving service contract are not valid unless
            agreed to in writing by the household goods carrier and the shipper.
            <br />
            (C) Household goods carriers will transport shipments with
            reasonable dispatch. Reasonable dispatch requires the transportation
            of a shipment within the agreed period of time shown on the moving
            services contract, except when circumstances beyond the carrier's
            control, force majeure, prevent or delay transportation.
            <br />
            (D) Moving services contracts must comply with all other applicable
            laws of the State of {mover.state}.
          </p>

          <p className="font-mono">
            <span className="font-bold">
              (2) Section 2 - Cargo Liability Provisions
            </span>
            <br />
            (A) The household goods carrier is liable for any loss or damage to
            the shipment, except as listed in subparagraphs (B) and (C) of this
            paragraph.
            <br />
            (B) The household goods carrier is not responsible for loss, damage,
            or delay due to acts of God, acts of civil authorities, defects in
            the shipment, a riot, a strike, or an act or default of the shipper.
            <br />
            (C) The household goods carrier is not liable for loss or damage
            caused by dangerous or explosive goods unless the shipper notifies
            unless the shipper notifies the carrier, in writing, of the nature
            of the goods and the carrier agrees, in writing, to the
            transportation of these goods.
          </p>

          <p className="font-mono">
            <span className="font-bold">(3) Section 3 - Claims Provisions</span>
            <br />
            (A) A written claim must be filed by the shipper within 90 days of
            delivery of the shipment to the final destination. In case of
            failure to make delivery, then a written claim must be filed by the
            shipper within 90 days after a reasonable time for delivery has
            elapsed.
            <br />
            (B) A household goods carrier is not liable for any claim that is
            not filed within 90 days of the delivery of the shipment to the
            final destination. A household goods carrier is not liable for any
            claim that is not filed within 90 days after a reasonable time for
            delivery has elapsed for shipments that were not delivered.
          </p>

          <p className="font-mono">
            <span className="font-bold">
              (4) Section 4 - Payment Provisions
            </span>
            <br />
            (A) The shipper agrees to pay the carrier for all services rendered
            and agrees to pay the carrier for any additional services rendered
            as a result of the shipper's failure to provide accurate and
            complete information.
          </p>

          <p className="font-mono">
            <span className="font-bold">
              (5) Section 5 - Provisions for Shipments Not Delivered.
            </span>
            <br />
            (A) A household goods carrier may place a shipment of household
            goods into storage if the shipper is not available for delivery of
            the goods as scheduled.
            <br />
            (B) The cost of such storage is the responsibility of the shipper of
            the household goods.
            <br />
            (C) A shipment of household goods placed in storage is subject to
            liens for storage, freight, and other lawful charges. storage,
            freight, and other lawful charges.
            <br />
            (D) A household goods carrier must issue written notice of the
            storage of the household goods to the shipper at each address shown
            on the moving services contract within three days of placing the
            goods in storage.
            <br />
            (E) If the shipper refuses to accept or does not claim the household
            goods within 15 days of the written notice of storage, the household
            goods carrier may begin the process of selling the goods at public
            sale, as prescribed in Transportation Code, Chapter 6.
            <br />
            (F) A household goods carrier must give written notice of the public
            sale to the shipper at each address shown on the moving services
            contract.
            <br />
            (G) The moving services contract does not prohibit the sale of the
            not prohibit the sale of the goods under any other lawful manner if
            the method set out in the contract cannot be reasonably
            accomplished.
          </p>
        </section>
      </div>
    </div>
  );
};

export default BillOfLading;
